// src/context/NetworkContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from '../utils/logger';

interface NetworkContextType {
  isOnline: boolean;
  refreshPage: () => void;
}

const NetworkContext = createContext<NetworkContextType>({
  isOnline: true,
  refreshPage: () => {},
});

export const useNetwork = () => useContext(NetworkContext);

export const NetworkProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [wasOffline, setWasOffline] = useState(false);

  // Function to refresh the page
  const refreshPage = () => {
    window.location.reload();
  };

  useEffect(() => {
    const handleOnline = () => {
      logger.info('Network connection restored');
      setIsOnline(true);
      
      // If we were previously offline, refresh the page automatically
      if (wasOffline) {
        logger.info('Auto-refreshing page after network restoration');
        setTimeout(() => {
          refreshPage();
        }, 1000); // Small delay to ensure connection is stable
      }
    };

    const handleOffline = () => {
      logger.warn('Network connection lost');
      setIsOnline(false);
      setWasOffline(true); // Remember that we were offline
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initialize state based on current status
    setIsOnline(typeof navigator !== 'undefined' ? navigator.onLine : true);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return (
    <NetworkContext.Provider value={{ isOnline, refreshPage }}>
      {children}
    </NetworkContext.Provider>
  );
};
